/**
 * Homepage slot 3 — the AI Visibility Checker, inline.
 *
 * The tool at /tools/llm-visibility-checker was only reachable from a nav
 * dropdown. It is the lowest-friction ask on the site — two fields against the
 * video review's eight — so it belongs on the homepage, high, where it can
 * catch the visitor who is interested but not ready to hand over a brief.
 *
 * This is a real entry point, not a link: it posts to the same
 * /api/tools/llm-visibility-checker/start endpoint and hands off to the same
 * loading page, so a submission here is indistinguishable downstream from one
 * made on the tool page itself. `formSource` carries the placement so Slack
 * can tell the two apart.
 *
 * The heading copy does double duty. It carries the "someone else is the
 * answer" argument in one sentence, which is why the standalone problem
 * section it replaced (ProblemChecklist, removed in the same change) is not
 * missed.
 *
 * Two implementation constraints:
 *
 * 1. scripts/prerender.mjs renders the homepage in headless Chromium at a
 *    390px viewport with isMobile: true, and captures `page.content()` after
 *    effects have run. A useIsMobile branch would therefore bake the mobile
 *    layout into the prerendered HTML and reflow on hydration. Responsive
 *    behavior here is CSS media queries only.
 *
 * 2. For the same reason the reveal is a one-shot CSS animation rather than a
 *    persistent hidden state — elements are opacity:1 by default and the
 *    observer only adds a class that plays a fade-and-rise. A baked-in
 *    `opacity: 0` would ship an invisible section; an animation can only fail
 *    to play.
 */

import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { formSource } from "@/lib/formSource";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

/** Matches the tool page's own list — kept short here; the full six live there. */
const PROMISES = [
  "Your AI Visibility Score, 0–100",
  "Competitor mentions revealed",
  "Citation sources and gaps",
  "Personalized action plan",
];

const PLATFORMS = ["ChatGPT", "Perplexity", "Google AI", "Gemini"];

function TickIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="6.7" stroke="var(--cyan)" strokeWidth="1.4" />
      <path d="M5.3 8.3L7.1 10.1L10.8 6.2" stroke="var(--cyan)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function VisibilityCheckerModule() {
  const root = useRef<HTMLElement>(null);
  const [, navigate] = useLocation();

  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Reveal on scroll. Bails to the static (already visible) state when the
  // observer is unavailable or the visitor prefers reduced motion.
  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const targets = el.querySelectorAll<HTMLElement>("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add("vcm-in");
          io.unobserve(e.target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Validation and normalization mirror VisibilityChecker.tsx exactly — the
  // endpoint expects a bare host, so a pasted https://www.example.com/pricing
  // has to arrive as example.com from either surface.
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    let cleanedDomain = domain.trim();
    const cleanedEmail = email.trim();

    if (!cleanedDomain) {
      setError("Please enter your website URL");
      return;
    }
    if (!cleanedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanedEmail)) {
      setError("Please enter a valid email address");
      return;
    }
    cleanedDomain = cleanedDomain
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/+$/, "")
      .split("/")[0];

    if (!cleanedDomain.includes(".")) {
      setError("Please enter a valid domain (e.g. yourbusiness.co.uk)");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/tools/llm-visibility-checker/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain: cleanedDomain,
          email: cleanedEmail,
          formSource: formSource("AI Visibility Checker", "Homepage module"),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }
      navigate(
        `/tools/llm-visibility-checker/loading?report_id=${data.report_id}&domain=${encodeURIComponent(cleanedDomain)}`,
      );
    } catch {
      setError("Network error. Please check your connection and try again.");
      setLoading(false);
    }
  }

  return (
    <section
      ref={root}
      className="vcm"
      aria-labelledby="vcm-heading"
      style={{ background: "#0a0c0c", position: "relative", overflow: "hidden", fontFamily: ff }}
    >
      <div
        className="glow-blob"
        aria-hidden="true"
        style={{ right: "-160px", top: "-120px", width: "540px", height: "540px", opacity: 0.12 }}
      />

      <div className="vcm-inner">
        {/* ── The pitch. Split from the rest of the copy so that on narrow
             screens the form can sit between them — heading, ask, then the
             supporting detail — rather than above the heading entirely. ── */}
        <div className="vcm-head" data-reveal>
          <div className="vcm-eyebrow">
            <span className="vcm-rule" aria-hidden="true" />
            <span>Free · 60 seconds · no call</span>
          </div>

          <h2 id="vcm-heading" className="vcm-h2">
            Find out what AI<br />
            <span style={{ color: "var(--cyan)" }}>says about you.</span>
          </h2>

          <p className="vcm-lead">
            Most companies have never checked. Then they find out an assistant has been recommending a
            competitor to their buyers for a year.
          </p>
        </div>

        {/* ── The supporting detail ── */}
        <div className="vcm-rest" data-reveal>
          <p className="vcm-sub">
            We run live queries — not cached data — score how often you are mentioned, show which rivals get
            named instead, and hand you a prioritized fix list.
          </p>

          <ul className="vcm-promises">
            {PROMISES.map((p) => (
              <li key={p}>
                <TickIcon />
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <div className="vcm-platforms">
            <span className="vcm-platforms-label">Queried live</span>
            {PLATFORMS.map((p) => (
              <span key={p} className="vcm-chip">{p}</span>
            ))}
          </div>
        </div>

        {/* ── Right: the ask ── */}
        <div className="vcm-card" data-reveal>
          <h3 className="vcm-card-h">Get your free AI brand report</h3>
          <p className="vcm-card-sub">
            We query live AI platforms and deliver a full report — in about 60 seconds.
          </p>

          <form onSubmit={handleSubmit} className="vcm-form">
            <div>
              <label htmlFor="vcm-domain" className="vcm-label">Your website</label>
              <input
                id="vcm-domain"
                name="domain"
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder="yourbusiness.co.uk"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={loading}
                className="vcm-field"
              />
            </div>

            <div>
              <label htmlFor="vcm-email" className="vcm-label">Work email</label>
              <input
                id="vcm-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="vcm-field"
              />
            </div>

            {error && <p role="alert" className="vcm-error">{error}</p>}

            <button type="submit" disabled={loading} className="btn-cyan vcm-submit">
              {loading ? "Starting your check…" : "Run my free visibility check"}
            </button>
          </form>

          <p className="vcm-reassure">No call. No pitch. The report lands in your inbox.</p>
        </div>
      </div>

      <style>{`
        .vcm { padding: 96px 72px; }
        .vcm-inner {
          max-width: 1136px; margin: 0 auto; position: relative;
          display: grid;
          grid-template-columns: 1.05fr 440px;
          /* The card spans both copy rows so it stays optically centered against
             the whole left column; on mobile the areas reorder to put it
             between the heading and the supporting detail. */
          grid-template-areas: "head card" "rest card";
          column-gap: 64px; row-gap: 0;
          align-items: start;
        }
        .vcm-head { grid-area: head; }
        .vcm-rest { grid-area: rest; }
        .vcm-card { grid-area: card; align-self: center; }

        .vcm-eyebrow {
          display: flex; align-items: center; gap: 12px; margin-bottom: 22px;
          color: var(--cyan); font-size: 11px; font-weight: 700;
          letter-spacing: 0.16em; text-transform: uppercase;
        }
        .vcm-rule { display: block; width: 28px; height: 1px; background: rgba(87,215,226,0.5); }

        .vcm-h2 {
          margin: 0; color: #fff; font-size: 46px; font-weight: 700;
          line-height: 1.08; letter-spacing: -0.03em;
        }
        .vcm-lead {
          margin: 20px 0 0; max-width: 560px;
          color: var(--text-muted); font-size: 17.5px; line-height: 1.65;
        }
        .vcm-sub {
          margin: 22px 0 0; max-width: 560px;
          color: rgba(255,255,255,0.5); font-size: 15.5px; line-height: 1.65;
        }

        .vcm-promises {
          list-style: none; margin: 28px 0 0; padding: 0; max-width: 560px;
          display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px;
        }
        .vcm-promises li {
          display: flex; align-items: center; gap: 10px;
          color: rgba(255,255,255,0.7); font-size: 14.5px; line-height: 1.4;
        }

        .vcm-platforms { display: flex; align-items: center; gap: 10px; margin-top: 30px; flex-wrap: wrap; }
        .vcm-platforms-label {
          color: rgba(255,255,255,0.35); font-size: 11px; font-weight: 700;
          letter-spacing: 0.12em; text-transform: uppercase; margin-right: 4px;
        }
        .vcm-chip {
          padding: 5px 13px; border-radius: 88px;
          border: 1px solid var(--border-glass-2); background: var(--fill-glass);
          color: rgba(255,255,255,0.78); font-size: 13px; font-weight: 600;
        }

        .vcm-card {
          box-sizing: border-box; padding: 32px 32px 30px;
          border: 1px solid var(--cyan-border-soft); border-radius: 22px;
          background: rgba(87,215,226,0.05);
          box-shadow: 0 24px 70px rgba(0,0,0,0.35);
        }
        .vcm-card-h { margin: 0; color: #fff; font-size: 21px; font-weight: 700; line-height: 1.25; }
        .vcm-card-sub {
          margin: 7px 0 24px; color: rgba(255,255,255,0.5); font-size: 14px; line-height: 1.5;
        }

        .vcm-form { display: flex; flex-direction: column; gap: 16px; }
        .vcm-label {
          display: block; margin-bottom: 7px;
          color: rgba(255,255,255,0.55); font-size: 11.5px; font-weight: 700;
          letter-spacing: 0.08em; text-transform: uppercase;
        }
        .vcm-field {
          width: 100%; box-sizing: border-box; height: 50px; padding: 0 16px;
          border: 1px solid var(--border-glass-2); border-radius: 12px;
          background: rgba(255,255,255,0.06); color: #fff;
          font-family: inherit; font-size: 15.5px;
          transition: border-color 0.18s ease;
        }
        .vcm-field::placeholder { color: rgba(255,255,255,0.38); }
        .vcm-field:focus { outline: none; border-color: rgba(87,215,226,0.55); }
        .vcm-field:focus-visible { outline: 2px solid var(--cyan); outline-offset: 2px; }
        .vcm-field:disabled { opacity: 0.6; }

        .vcm-error { margin: 0; color: #ff847e; font-size: 13.5px; line-height: 1.45; }

        .vcm-submit {
          height: 54px; margin-top: 4px; width: 100%;
          font-size: 16px; cursor: pointer;
        }
        .vcm-submit:disabled { opacity: 0.65; cursor: default; transform: none; }

        .vcm-reassure {
          margin: 14px 0 0; text-align: center;
          color: rgba(255,255,255,0.42); font-size: 12.5px; line-height: 1.5;
        }

        @media (max-width: 1023px) {
          .vcm { padding: 60px 20px; }
          .vcm-inner {
            grid-template-columns: 1fr;
            grid-template-areas: "head" "card" "rest";
            row-gap: 24px;
          }
          /* The ask sits directly under the heading on narrow screens: a
             two-field form is not worth a scroll of supporting copy to reach,
             but it still needs the headline above it to make sense. */
          .vcm-card { padding: 22px 20px 20px; }
          .vcm-h2 { font-size: 33px; }
          .vcm-lead { font-size: 16px; }
          .vcm-sub { font-size: 15px; margin-top: 0; }
          .vcm-promises { grid-template-columns: 1fr; gap: 11px; margin-top: 22px; }
        }

        /* Reveal — see the file header for why this is an animation and not a
           persistent hidden state. */
        @keyframes vcmRise {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: none; }
        }
        .vcm [data-reveal].vcm-in {
          animation: vcmRise 0.8s cubic-bezier(0.2, 0.6, 0.2, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .vcm [data-reveal].vcm-in { animation: none; }
        }
      `}</style>
    </section>
  );
}
